/**
 * SimpleCategoryDetails.js - Simplified category details hook
 *
 * This hook directly fetches channels for a category without any gates.
 */
import { useState, useEffect, useCallback } from 'react';
import { useAppLoad } from '../contexts/AppLoadContext';
import spotlightService from '../services/SpotlightService';

export const useSimpleCategoryDetails = (categoryIdentifier, isSlug = false) => {
  const [category, setCategory] = useState(null);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { apiUrl, loading: appLoadLoading, error: appLoadError } = useAppLoad();

  const fetchData = useCallback(async () => {
    if (appLoadLoading || !apiUrl || !categoryIdentifier) {
      setLoading(appLoadLoading);
      return;
    }

    if (appLoadError) {
      setError(`AppLoad failed: ${appLoadError}`);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Make two API calls: one for category data (wallpaper/logo) and one for full channel data (modals)
      console.log('🔧 useSimpleCategoryDetails: Making dual API calls for', categoryIdentifier);
      
      const [categoryData, channelsData] = await Promise.all([
        spotlightService.callAPI(`categories/US/${categoryIdentifier}`),
        spotlightService.callAPI(`channels/US/${categoryIdentifier}?include_subscription_access=true`)
      ]);
      
      console.log('🔧 Category API response:', categoryData ? 'Success' : 'Failed');
      console.log('🔧 Channels API response:', channelsData ? 'Success' : 'Failed');
      
      if (categoryData && categoryData.category && channelsData && channelsData.channels) {
        console.log('🔧 Channels count:', channelsData.channels.length);
        console.log('🔧 First channel video_id:', channelsData.channels[0]?.video_id);
        console.log('🔧 First channel keys:', Object.keys(channelsData.channels[0] || {}));
        
        // Use the full channel data from channels API (has all properties for modals)
        // Map the video._id property to video_id for compatibility with PDPModal
        const mappedChannels = channelsData.channels.map(channel => ({
          ...channel,
          video_id: channel.video?._id || channel.video_id // Use video._id as video_id for PDPModal
        }));
        
        console.log('🔧 Video._id:', channelsData.channels[0]?.video?._id);
        console.log('🔧 Mapped first channel video_id:', mappedChannels[0]?.video_id);
        setChannels(mappedChannels);
        
        // Use the complete category data from categories API (includes logo and wallpaper)
        setCategory({
          name: categoryData.category.name || categoryIdentifier.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          display_name: categoryData.category.display_name || categoryData.category.name,
          slug: categoryData.category.slug || categoryIdentifier,
          _id: categoryData.category._id,
          logo: categoryData.category.logo,
          wallpaper: categoryData.category.wallpaper,
          description: categoryData.category.description
        });
      } else {
        console.error('🔧 API calls failed:', { 
          categoryData: !!categoryData, 
          hasCategory: !!(categoryData?.category),
          channelsData: !!channelsData, 
          hasChannels: !!(channelsData?.channels)
        });
        setChannels([]);
        setCategory(null);
        throw new Error(`Category or channels not found: ${categoryIdentifier}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, categoryIdentifier, isSlug, appLoadLoading, appLoadError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    category,
    channels,
    loading,
    error,
    retry: fetchData,
  };
};

